import { fetchAllPages, CrmApiRepository } from './crm/CrmApiRepository.js';
import { UsersApiService } from './crm/UsersApiService.js';
import { mapUserFromApi, mapUserToApi } from './crm/ApiEntityMapper.js';
import { generateId, nowIso } from '../utils/IdUtils.js';
import { auth } from './AuthService.js';
import { AuditService } from './AuditService.js';
import { formatDateDisplay } from '../utils/Utils.js';
import { ROLE_LABELS, filterAssignableUsers } from '../constants/Roles.js';
import { RoleMetadataService } from './RoleMetadataService.js';
import { USER_DEPARTMENTS } from '../constants/UserDepartments.js';
import { USER_DESIGNATIONS } from '../constants/UserDesignations.js';
import { WORK_LOCATIONS } from '../constants/WorkLocations.js';
import { CacheService } from './CacheService.js';
import { EMAIL_ERROR_MESSAGE, isValidEmail } from '../utils/EmailValidation.js';
import { ReferenceDataStore } from '../reference/ReferenceDataStore.js';
import '../reference/SyncManager.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuid(value) {
    return UUID_RE.test(String(value || ''));
}

function userFromSession() {
    const session = auth.getSession();
    if (!session?.userId) return null;
    return {
        id: session.userId,
        name: session.name || '',
        email: session.email || '',
        role: session.role || 'viewer',
        department: session.department || '',
        status: session.status || 'active',
        avatarUrl: session.avatarUrl || ''
    };
}

export class UserService {
    static async getAll() {
        const rows = await fetchAllPages('/api/v1/users');
        return rows.map(mapUserFromApi);
    }

    static async getLookupUsers() {
        return ReferenceDataStore.ensureUsersLookup();
    }

    static async getActive() {
        const all = await UserService.getLookupUsers().catch(() => UserService.getAll());
        return all.filter(u => u.status === 'active');
    }

    static async getAssignableUsers({ includeUserIds = [] } = {}) {
        await RoleMetadataService.ensureLoaded();
        try {
            return await ReferenceDataStore.ensureUsersAssignable({ includeUserIds });
        } catch {
            const active = await UserService.getActive();
            return filterAssignableUsers(active, { includeUserIds });
        }
    }

    static async getById(id) {
        if (!isUuid(id)) {
            const session = auth.getSession();
            if (session?.userId === id) return userFromSession();
            return null;
        }
        const session = auth.getSession();
        if (session?.userId && String(session.userId) === String(id)) {
            const row = await UsersApiService.getProfile();
            return mapUserFromApi(row);
        }
        const row = await UsersApiService.getById(id);
        return mapUserFromApi(row);
    }

    static async getEnrichedAll() {
        const cacheKey = 'users:enriched:all';
        return CacheService.getOrSet(cacheKey, async () => {
            const users = await UserService.getAll();
            const userMap = Object.fromEntries(users.map(u => [u.id, u.name]));
            return users.map(u => ({
                ...u,
                roleLabel: ROLE_LABELS[u.role] || u.role,
                departmentLabel: USER_DEPARTMENTS[u.department] || u.department || '—',
                designationLabel: USER_DESIGNATIONS[u.designation] || u.designation || '—',
                workLocationLabel: WORK_LOCATIONS[u.workLocation] || u.workLocation || '—',
                managerName: userMap[u.managerUserId] || '—',
                createdByName: userMap[u.createdBy] || '—',
                updatedByName: userMap[u.updatedBy] || '—',
                createdAtDisplay: formatDateDisplay(u.createdAt),
                updatedAtDisplay: formatDateDisplay(u.updatedAt),
                lastLoginDisplay: u.lastLoginAt ? formatDateDisplay(u.lastLoginAt) : '—',
                isCommissionEligibleDisplay: u.isCommissionEligible === false ? 'No' : 'Yes'
            }));
        }, 5 * 60 * 1000);
    }

    static async recordLastLogin() {
        return null;
    }

    static async save(user) {
        const ts = nowIso();
        const prev = user.id ? await UserService.getById(user.id) : null;
        const session = auth.getSession();
        const record = {
            id: user.id || generateId('user'),
            name: user.name?.trim(),
            email: user.email?.trim().toLowerCase(),
            phone: user.phone?.trim() || '',
            role: user.role || 'sales_executive',
            department: user.department || '',
            designation: user.designation || '',
            managerUserId: user.managerUserId || '',
            isCommissionEligible: user.isCommissionEligible !== false,
            workLocation: user.workLocation || '',
            status: user.status || 'active',
            avatarUrl: user.avatarUrl || prev?.avatarUrl || '',
            createdBy: prev?.createdBy || user.createdBy || session?.userId || null,
            updatedBy: session?.userId || user.updatedBy || null,
            createdAt: prev?.createdAt || user.createdAt || ts,
            updatedAt: ts
        };
        if (!record.name || !record.email) throw new Error('Name and email are required.');
        if (!isValidEmail(record.email)) throw new Error(EMAIL_ERROR_MESSAGE);
        if (!record.role) throw new Error('Role is required.');

        const apiPayload = mapUserToApi({
            ...record,
            password: user.password ? String(user.password).trim() : undefined
        }, { includePassword: true });
        if (user.id && prev) {
            const prevEligible = prev.isCommissionEligible !== false;
            const nextEligible = record.isCommissionEligible !== false;
            if (prevEligible === nextEligible) {
                delete apiPayload.is_commission_eligible;
            }
        }
        let saved;
        if (user.id) {
            const session = auth.getSession();
            const isSelf = session?.userId && String(session.userId) === String(user.id);
            const emailChanged = !!(prev && record.email
                && record.email !== String(prev.email || '').trim().toLowerCase());
            const roleChanged = !!(prev && record.role && record.role !== prev.role);
            // /users/me only accepts name/phone. Email, role, and other admin fields
            // must go through PUT /users/{id}.
            if (isSelf && !emailChanged && !roleChanged) {
                saved = await UsersApiService.updateProfile({
                    full_name: apiPayload.full_name,
                    phone: apiPayload.phone,
                });
            } else {
                saved = await UserService._updateWithRoleOverrideGuard(user.id, apiPayload, prev);
            }
        } else {
            try {
                saved = await UsersApiService.create(apiPayload);
            } catch (err) {
                const msg = String(err?.message || '').toLowerCase();
                if (!msg.includes('transaction is already begun')) throw err;
                // Backend occasionally returns transaction-state errors on first create.
                // Retry once with the same payload.
                saved = await UsersApiService.create(apiPayload);
            }
        }
        Object.assign(record, mapUserFromApi(saved));
        // Prefer the name/email we just submitted — protects against stale API responses
        const submittedName = String(user.name || '').trim();
        if (submittedName) record.name = submittedName;
        const submittedEmail = String(user.email || '').trim().toLowerCase();
        if (submittedEmail) record.email = submittedEmail;
        if (!record.id && user.id) record.id = user.id;

        if (prev) {
            await AuditService.logFieldChanges({
                entityType: 'user',
                entityId: record.id,
                action: 'user.fields_changed',
                before: prev,
                after: record,
                fields: ['name', 'email', 'role', 'status']
            });
        } else {
            await AuditService.log({
                entityType: 'user',
                entityId: record.id,
                action: 'user.created',
                after: { name: record.name, role: record.role }
            });
        }
        window.dispatchEvent(new CustomEvent('userSaved', { detail: { user: record } }));
        return record;
    }

    /**
     * PUT /users/{id} with role-change override confirmation.
     * On 409 ROLE_CHANGE_CLEARS_OVERRIDES: confirm → retry with clear flag; cancel → abort entirely.
     */
    static async _updateWithRoleOverrideGuard(userId, apiPayload, prev) {
        const roleChanging = !!(prev && apiPayload.role && apiPayload.role !== prev.role);
        try {
            return await UsersApiService.update(userId, apiPayload);
        } catch (err) {
            if (!roleChanging || err?.code !== 'ROLE_CHANGE_CLEARS_OVERRIDES') throw err;
            const d = err.details || {};
            const granted = Number(d.granted_count) || 0;
            const restricted = Number(d.restricted_count) || 0;
            const scopeDiffs = Number(d.scope_diff_count) || 0;
            const total = Number(d.override_count) || (granted + restricted + scopeDiffs);
            let detail = `${granted} granted, ${restricted} restricted`;
            if (scopeDiffs > 0) detail += `, ${scopeDiffs} scope`;
            const ok = confirm(
                `This user has ${total} custom permission override${total === 1 ? '' : 's'} (${detail}). `
                + 'Changing their role will clear these overrides. Continue?'
            );
            if (!ok) {
                const cancelErr = new Error(
                    'Role change cancelled — overrides were kept and the role was not changed.'
                );
                cancelErr.code = 'ROLE_CHANGE_CANCELLED';
                throw cancelErr;
            }
            return await UsersApiService.update(userId, {
                ...apiPayload,
                clear_permission_overrides: true,
            });
        }
    }

    static async delete(id) {
        const prev = await UserService.getById(id);
        if (!prev) return;
        window._suppressRealtimeToastFor?.('user.deleted', id);
        window._suppressRealtimeRefreshFor?.('user.deleted', id);
        await UsersApiService.delete(id);
        CrmApiRepository.clearCache();
        await AuditService.log({
            entityType: 'user',
            entityId: id,
            action: 'user.disabled',
            before: { status: prev.status },
            after: { status: 'inactive' }
        });
        window.dispatchEvent(new CustomEvent('userDeleted', { detail: { id } }));
    }
}
